import React from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { useConfig } from '@ynap/immutables-provider';
import { formatPrice } from '@ynap/intl-formatters';
import { promotionType, marketingKey } from '../promoConstants';

const bem = new BEMHelper('PriceWithSchema');

const propTypes = {
    currency: shape({
        symbol: string.isRequired,
        label: string.isRequired,
    }).isRequired,
    messages: shape({
        price: shape({
            discountPrice: func.isRequired,
            currencyExchange: func,
            discountPercentage: func,
            ddpInformation: func,
        }).isRequired,
    }),
    className: string,
    originalPrice: number,
    discountPercentage: number,
    finalPrice: number.isRequired,
    finalPriceWithoutCurrencySymbol: number,
    loading: bool,
    exchangePrice: number,
    exchangeCurrency: string,
    details: bool,
    listingPage: bool,
    designerName: string,
    badgeKey: string,
    locale: string,
    ddpIncludedThreshold: bool,
    priceSuffix: string,
    promoPriceApplied: string,
    modifiers: shape({
        small: bool,
    }),
    minimumPrice: number,
    minPriceDiscount: number,
    setModal: func,
    isWishlist: bool,
    country: string,
    omnibusAlignment: bool,
    searchResults: bool,
    isShoppingBag: bool,
    saleExclusive: bool,
};

const defaultProps = {
    availability: 'InStock',
    locale: 'en',
};

const PriceWithSchema = (props) => {
    const {
        badgeKey,
        className,
        country,
        currency = { symbol: '', label: '' },
        ddpIncludedThreshold = false,
        designerName,
        details,
        discountPercentage,
        exchangeCurrency,
        exchangePrice,
        finalPrice,
        finalPriceWithoutCurrencySymbol,
        hasAutoApplyPromo,
        isWishlist = false,
        listingPage,
        loading = false,
        locale,
        marketingProperties,
        messages,
        minimumPrice,
        minPriceDiscount,
        modifiers: { small } = {},
        omnibusAlignment,
        originalPrice,
        priceSuffix = null,
        promoPriceApplied,
        promotionApplied,
        saleExclusive,
        searchResults,
        setModal,
        soldOut,
    } = props;

    const config = useConfig();
    const minimumPriceCountries = config?.head?.minimumPriceCountries;
    const isMinimumPriceCountry = minimumPriceCountries?.includes(country?.toUpperCase?.());
    const omnibusROWEnabled = config?.head?.omnibusROWEnabled;

    if (loading) return <div className={bem(null, 'loading', className)} />;

    const privateLabel = designerName === 'IRIS & INK' || badgeKey === 'BADGE_EXCLUSIVE';

    // PRIVATE LABEL CONDITIONS
    const privateLabelWithoutMinimumPrice = privateLabel && !minimumPrice && omnibusROWEnabled;
    const privateLabelWithMinimumPrice = privateLabel && minimumPrice && omnibusROWEnabled;

    // THIRD PARTY CONDITIONS
    const thirdPartyWithoutMinimumPrice = !privateLabel && !minimumPrice && omnibusROWEnabled && country !== 'US';
    const thirdPartyWithMinimumPrice = !privateLabel && minimumPrice && omnibusROWEnabled;

    const sale = !!discountPercentage;
    const listingPageOriginalPrice = (privateLabelWithoutMinimumPrice || privateLabelWithMinimumPrice) && listingPage;
    const modifiers = { sale, details, promoPriceApplied, listingPage, listingPageOriginalPrice, small, omnibusAlignment, searchResults, hasAutoApplyPromo };
    const getPriceToShow = (price) => currency?.label && price && formatPrice(price, currency?.label, locale);

    const getMinimumPriceMessage = (minimumPrice, minPriceDiscount, finalPrice) => {
        if (minimumPrice && minimumPrice < finalPrice) {
            return (
                <span>
                    {messages?.price?.minimumPrice?.()} {getPriceToShow(minimumPrice)}
                </span>
            );
        } 
        
        if (minimumPrice && minimumPrice > finalPrice) {
            return (
                <span>
                    <strong>
                        {messages?.price?.minimumPrice?.()} <s>{getPriceToShow(minimumPrice)}</s>{' '}
                        {messages?.price?.discountPercentage?.({ discountPercentage: minPriceDiscount })}
                    </strong>
                </span>
            );
        } 
        
        return <span>{messages?.price?.minimumPriceEqual?.()}</span>;
        
    };

    const formatExchangePrice = exchangeCurrency && exchangePrice && formatPrice(exchangePrice, exchangeCurrency, locale.slice(0, 2));

    const approxPrice =
        formatExchangePrice &&
        messages.price.currencyExchange({
            exchangeCurrency: '', // TODO remove once removed from message bundles.
            exchangePrice: formatExchangePrice,
        });

    const renderDiscountLabels = () => {
        if (privateLabelWithoutMinimumPrice || privateLabelWithMinimumPrice) {
            return (
                <>
                    <span className={bem('priceMessage')}>{messages?.price?.originalPrice?.()}</span>
                    <span className={bem('previousPrice')}>{getPriceToShow(originalPrice)}</span>
                </>
            );
        } 
        
        if (thirdPartyWithoutMinimumPrice || thirdPartyWithMinimumPrice) {
            return (
                <>
                    <span className={bem('priceMessage')}>{messages?.price?.wasPrice?.()}</span>
                    <span className={bem('previousPrice')}>{getPriceToShow(originalPrice)}</span>
                </>
            );
        } 
        
        return (
            <span
                className={bem('previousPrice', {
                    noStrikethrough: isMinimumPriceCountry || country === 'US',
                })}
            >
                {(isMinimumPriceCountry && messages?.price?.wasPriceOriginal?.({ wasPrice: getPriceToShow(originalPrice) })) ||
                    getPriceToShow(originalPrice)}
            </span>
        );
        
    };

    const validAutoAppliedPromotion = promotionApplied === promotionType.AUTO && marketingProperties[marketingKey.CODE_DESCRIPTION];
    const isManualAppliedPromotion = promotionApplied === promotionType.MANUAL;
    const valueModifiers = {
        ...modifiers,
        sale: validAutoAppliedPromotion ? undefined : modifiers.sale,
        strikeThrough: validAutoAppliedPromotion,
        normalFontWeight: validAutoAppliedPromotion,
        autoApplyPromo: hasAutoApplyPromo,
        manualApplyPromo: isManualAppliedPromotion,
    };

    const noSaleModifiers = { sale, details, listingPage, small, privateLabelWithoutMinimumPrice };

    if (sale) {
        return (
            <div className={bem(null, modifiers, className)}>
                <div className={bem('discountContainer', modifiers)}>
                    {renderDiscountLabels()}
                    <div className={bem('discount', modifiers)}>{messages?.price?.discountPercentage({ discountPercentage })}</div>
                    {!isWishlist && details && (
                        <button
                            className={bem('priceDetailsIcon', ['discount', soldOut && 'soldOut'].filter(Boolean))}
                            onClick={() => setModal('priceInformation')}
                        />
                    )}
                </div>
                <div>
                    <span className={bem('value', valueModifiers)}>
                        <span content={finalPriceWithoutCurrencySymbol}>{getPriceToShow(finalPrice)}</span>
                        {priceSuffix && <span className={bem('priceSuffix')}>{priceSuffix}</span>}
                        {!!approxPrice && <span className={bem('exchange', modifiers)}>{approxPrice}</span>}
                        {ddpIncludedThreshold && <span className={bem('ddpInformation')}>{messages.price.ddpInformation()}</span>}
                    </span>
                    {validAutoAppliedPromotion ? (
                        <span className={bem('value', { ...modifiers, padding: true, autoPromoValue: true })}>{marketingProperties[marketingKey.CODE_DESCRIPTION]}</span>
                    ) : null}
                </div>
                {isMinimumPriceCountry && (
                    <div className={bem('minimumPriceContainer', [omnibusAlignment ? 'summary' : '', saleExclusive ? '' : 'hide'])}>
                        {getMinimumPriceMessage(minimumPrice, minPriceDiscount, finalPrice)}
                        {!isWishlist && <button className={bem('priceDetailsIcon')} onClick={() => setModal('priceInformation')} />}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={bem(null, modifiers, className)}>
            <span className={bem('value', noSaleModifiers)}>
                <span
                    className={bem('value', { listingPage, strikeThrough: validAutoAppliedPromotion, normalFontWeight: validAutoAppliedPromotion })}
                    content={finalPriceWithoutCurrencySymbol}
                >
                    {getPriceToShow(finalPrice)}
                </span>
                {priceSuffix && <span className={bem('priceSuffix')}>{priceSuffix}</span>}
                {!!approxPrice && <span className={bem('exchange')}>{approxPrice}</span>}
                {ddpIncludedThreshold && <span className={bem('ddpInformation')}>{messages.price.ddpInformation()}</span>}
                {validAutoAppliedPromotion ? (
                    <span className={bem('value', { sale: true, padding: true, strikeThrough: false, normalFontWeight: true })}>
                        {marketingProperties[marketingKey.CODE_DESCRIPTION]}
                    </span>
                ) : null}
            </span>
        </div>
    );
};

PriceWithSchema.propTypes = propTypes;
PriceWithSchema.defaultProps = defaultProps;

export default PriceWithSchema;
